import { Activity } from '@/types';

export class ActivityForm {
  private element: HTMLElement;
  private onSubmit: (activity: Omit<Activity, 'id' | 'createdAt'>) => void;
  private onCancel: () => void;
  private editingActivity: Activity | null = null;

  constructor(callbacks: {
    onSubmit: (activity: Omit<Activity, 'id' | 'createdAt'>) => void;
    onCancel: () => void;
  }) {
    this.onSubmit = callbacks.onSubmit;
    this.onCancel = callbacks.onCancel;
    this.element = document.createElement('div');
    this.render();
    this.attachEventListeners();
  }

  private render(): void {
    this.element.className = 'modal-overlay';
    this.element.innerHTML = `
      <div class="modal-content">
        <div class="flex items-center justify-between mb-6">
          <h2 class="font-bold text-xl">
            ${this.editingActivity ? 'Edit Activity' : 'Add New Activity'}
          </h2>
          <button class="btn btn-secondary btn-sm cancel-btn">✕</button>
        </div>
        
        <form class="activity-form">
          <div class="form-group">
            <label class="form-label">Activity Name</label>
            <input 
              type="text" 
              class="form-input" 
              name="name" 
              required
              placeholder="e.g., Morning workout"
              value="${this.editingActivity?.name || ''}"
            />
          </div>
          
          <div class="form-group">
            <label class="form-label">Description (optional)</label>
            <input 
              type="text" 
              class="form-input" 
              name="description"
              placeholder="Brief description of the activity"
              value="${this.editingActivity?.description || ''}"
            />
          </div>
          
          <div class="grid grid-2 gap-4">
            <div class="form-group">
              <label class="form-label">Category</label>
              <select class="form-select" name="category" required>
                <option value="">Select category</option>
                <option value="habit" ${this.editingActivity?.category === 'habit' ? 'selected' : ''}>
                  🎯 Habit
                </option>
                <option value="study" ${this.editingActivity?.category === 'study' ? 'selected' : ''}>
                  📚 Study
                </option>
                <option value="fitness" ${this.editingActivity?.category === 'fitness' ? 'selected' : ''}>
                  💪 Fitness
                </option>
                <option value="work" ${this.editingActivity?.category === 'work' ? 'selected' : ''}>
                  💼 Work
                </option>
                <option value="personal" ${this.editingActivity?.category === 'personal' ? 'selected' : ''}>
                  ❤️ Personal
                </option>
              </select>
            </div>
            
            <div class="form-group">
              <label class="form-label">Target Frequency</label>
              <select class="form-select" name="targetFrequency" required>
                <option value="">Select frequency</option>
                <option value="daily" ${this.editingActivity?.targetFrequency === 'daily' ? 'selected' : ''}>
                  Daily
                </option>
                <option value="weekly" ${this.editingActivity?.targetFrequency === 'weekly' ? 'selected' : ''}>
                  Weekly
                </option>
                <option value="monthly" ${this.editingActivity?.targetFrequency === 'monthly' ? 'selected' : ''}>
                  Monthly
                </option>
              </select>
            </div>
          </div>
          
          <div class="grid grid-2 gap-4">
            <div class="form-group">
              <label class="form-label">Points per Completion</label>
              <input 
                type="number" 
                class="form-input" 
                name="pointsPerCompletion" 
                min="1" 
                max="100"
                required
                placeholder="10"
                value="${this.editingActivity?.pointsPerCompletion || ''}"
              />
            </div>
            
            <div class="form-group">
              <label class="form-label">Icon (optional)</label>
              <input 
                type="text" 
                class="form-input" 
                name="icon"
                placeholder="🎯"
                maxlength="2"
                value="${this.editingActivity?.icon || ''}"
              />
            </div>
          </div>
          
          <div class="form-group">
            <label class="form-label">
              <input 
                type="checkbox" 
                name="isActive"
                ${this.editingActivity?.isActive !== false ? 'checked' : ''}
              />
              Active (uncheck to pause this activity)
            </label>
          </div>
          
          <div class="flex gap-3 mt-6">
            <button type="submit" class="btn btn-primary flex-1">
              ${this.editingActivity ? 'Update Activity' : 'Create Activity'}
            </button>
            <button type="button" class="btn btn-secondary cancel-btn">
              Cancel
            </button>
          </div>
        </form>
      </div>
    `;
  }

  private attachEventListeners(): void {
    const form = this.element.querySelector('.activity-form') as HTMLFormElement;
    const cancelBtns = this.element.querySelectorAll('.cancel-btn');

    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      
      const formData = new FormData(form);
      const activity: Omit<Activity, 'id' | 'createdAt'> = {
        name: formData.get('name') as string,
        description: formData.get('description') as string || undefined,
        category: formData.get('category') as Activity['category'],
        targetFrequency: formData.get('targetFrequency') as Activity['targetFrequency'],
        pointsPerCompletion: parseInt(formData.get('pointsPerCompletion') as string),
        isActive: formData.get('isActive') === 'on',
        icon: formData.get('icon') as string || undefined,
      };

      this.onSubmit(activity);
    });

    cancelBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        this.onCancel();
      });
    });

    this.element.addEventListener('click', (e) => {
      if (e.target === this.element) {
        this.onCancel();
      }
    });
  }

  public setEditingActivity(activity: Activity | null): void {
    this.editingActivity = activity;
    this.render();
    this.attachEventListeners();
  }

  public getElement(): HTMLElement {
    return this.element;
  }
}